import React from "react";
import clsx from "clsx";
import { format } from "date-fns";

import {
  ASSESSMENT_CONTENTS,
  X_LABELS_JSX,
} from "../../../constants/assessment-content";
import {
  IAssessmentTeacher,
  IAssessment,
  IAssessmentAnswer,
} from "../../../state";
import { API_URL } from "../../../teacherStore";
import { ReactComponent as ArrowLeftIcon } from "../../../assets/svg/keyboard_arrow_left.svg";
import { ReactComponent as ArrowRightIcon } from "../../../assets/svg/keyboard_arrow_right.svg";
import { ReactComponent as ArrowDownIcon } from "../../../assets/svg/keyboard_arrow_down.svg";
import { ReactComponent as CloseIcon } from "../../../assets/svg/close.svg";
import { NextActionKinds } from "../../../constants/enum";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import AssessmentExperienceLabel, {
  LABEL_COLORS,
} from "../../../atoms/AssessmentExperienceLabel";
import BarChartTeacher from "../../../atoms/BarChartTeacher";
import DropdownTerm, { ITerm } from "../../../atoms/DropdownTerm";
import Spinner from "../../../atoms/Spinner";
import ModalConfirm from "../../../molecules/ModalConfirm";
import { formatDate } from "../../../libs/Util";

import styles from "./Assessment.module.scss";

export interface IAssessmentScore {
  term_no: number;
  scores: number[];
}

const QUESTION_TITLE = ["どんな事に気づき、学べたか", "どう活かせるか？"];

export type Props = {
  pdfToken: string | null;
  calling: boolean;
  classID: number;
  currentStudent: { id: number; full_name: string };
  students: { id: number; full_name: string }[];
  assessment: IAssessmentTeacher;
  assessmentsPast: IAssessment[];
  assessmentAnswers: IAssessmentAnswer[];
  onClose: () => void;
  onSubmitFeedback: (feedback_comment: string) => void;
  onPrevStudent: () => void;
  onNextStudent: () => void;
  onDownloadPDF: () => void;
  onClearPDFToken: () => void;
};

const getTermName = (assessment: { term_no: number; created_at: string }) =>
  `${assessment.term_no}回目 (${formatDate(assessment.created_at, "/")})`;

const getAverageScore = (assessment: IAssessmentTeacher | IAssessment) => {
  const scores = assessment.assessment_experiences
    .filter(a => a.score !== null)
    .map(a => a.score ?? 0);

  if (scores.length > 0) {
    const sum = scores.reduce((p, c) => p + c, 0);
    const average = sum / scores.length;
    return Math.round(average * 10) / 10;
  } else {
    return null;
  }
};

const Assessment: React.FC<Props> = ({
  calling,
  pdfToken,
  classID,
  currentStudent,
  students,
  assessment,
  assessmentsPast,
  assessmentAnswers,
  onClose,
  onSubmitFeedback,
  onPrevStudent,
  onNextStudent,
  onDownloadPDF,
  onClearPDFToken,
}) => {
  const [feedback, setFeedback] = React.useState("");
  const [previousTerms, setPreviousTerms] = React.useState<ITerm[]>([]);
  const [selectedTerms, setSelectedTerms] = React.useState<number[]>([]);
  const [
    currentDataChart,
    setCurrentDataChart,
  ] = React.useState<IAssessmentScore | null>(null);
  const [pastDataStudentChart, setPastDataStudentChart] = React.useState<
    IAssessmentScore[]
  >([]);
  const [selectedExperienceNo, setSelectedExperienceNo] = React.useState(0);
  const [debounced] = useDebounceCallback(() => onDownloadPDF());

  const averageScore = React.useMemo(() => {
    return getAverageScore(assessment);
  }, [assessment]);

  const previousAverageScore = React.useMemo(() => {
    const previousAssessment = assessmentsPast[0];
    return previousAssessment ? getAverageScore(previousAssessment) : null;
  }, [assessmentsPast]);

  const averageScoreText = React.useMemo(() => {
    return averageScore === null ? "" : averageScore.toFixed(1);
  }, [averageScore]);

  const difference = React.useMemo(() => {
    return averageScore === null
      ? null
      : previousAverageScore === null
      ? null
      : averageScore - previousAverageScore;
  }, [averageScore, previousAverageScore]);

  const nextActionKindsWithFilteredNextActions = React.useMemo(
    () =>
      NextActionKinds.map(k => {
        const currentNextActions = assessment.current_assessment_next_actions.filter(
          n => n.kind === k.kind,
        );
        const pastNextActions = assessment.past_assessment_next_actions.filter(
          n => n.kind === k.kind,
        );
        return { ...k, currentNextActions, pastNextActions };
      }),
    [assessment],
  );

  const filteredReasons = React.useMemo(() => {
    if (!assessment.reasons?.length) return [];
    return selectedExperienceNo === 0
      ? assessment.reasons
      : assessment.reasons.filter(
          r => r.experience_no === selectedExperienceNo,
        );
  }, [assessment, selectedExperienceNo]);

  React.useEffect(() => {
    const terms: ITerm[] = assessmentsPast.map(a => ({
      term_no: a.term_no,
      name: getTermName(a),
    }));
    setPreviousTerms(terms);
  }, [assessmentsPast]);

  React.useEffect(() => {
    if (assessment.id) {
      const { assessment_experiences, term_no } = assessment;
      const studentScoresCurrent: number[] = assessment_experiences.map(
        item => item.score,
      );
      setCurrentDataChart({
        term_no,
        scores: studentScoresCurrent,
      });
      setFeedback(assessment.feedback_comment || "");
    } else {
      setCurrentDataChart({
        term_no: 0,
        scores: [0, 0, 0, 0, 0, 0, 0],
      });
      setFeedback("");
    }
    if (assessmentsPast) {
      const dataStudentPast: IAssessmentScore[] = [];
      const assessmentsPastSelected: IAssessment[] = assessmentsPast.filter(
        ass => selectedTerms.includes(ass.term_no),
      );
      const dataEmpty = selectedTerms
        .filter(st => !assessmentsPast.map(ap => ap.term_no).includes(st))
        .map(st => ({
          assessment_experiences: Array.from(Array(8).keys()).map(i => ({
            experience_no: i + 1,
            score: 0,
          })),
          term_no: st,
        }));

      ([...assessmentsPastSelected, ...dataEmpty] as any[]).forEach(
        ({ assessment_experiences, term_no }) => {
          const studentScores: number[] = assessment_experiences.map(
            (item: { score: number }) => (item.score ? item.score : 0),
          );
          dataStudentPast.push({ term_no, scores: studentScores });
        },
      );
      setPastDataStudentChart(
        [...dataStudentPast].sort((a, b) => a.term_no - b.term_no),
      );
    } else {
      setPastDataStudentChart([]);
    }
  }, [selectedTerms, assessment, assessmentsPast]);

  return (
    <div className={clsx(styles.wrapper)}>
      <div className={styles.wrapperContent}>
        {calling && <Spinner className={styles.loading} />}
        <div className={styles.headerArea}>
          <div className={styles.pageButtons}>
            {students.length > 1 && (
              <button
                className={styles.arrowLeft}
                onClick={() => {
                  onPrevStudent();
                  setSelectedTerms([]);
                }}
              >
                <ArrowLeftIcon />
              </button>
            )}
            {students.length > 1 && (
              <button
                className={styles.arrowRight}
                onClick={() => {
                  onNextStudent();
                  setSelectedTerms([]);
                }}
              >
                <ArrowRightIcon />
              </button>
            )}
          </div>
          <button className={styles.close} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.studentNameArea}>
          <span>{currentStudent.full_name}</span>
          <button
            className={clsx(
              styles.downloadPDF,
              !assessment.id && !assessmentsPast.length && styles.disabled,
            )}
            disabled={calling || (!assessment.id && !assessmentsPast.length)}
            onClick={debounced}
          >
            PDFダウンロード
          </button>
        </div>
        <div className={styles.averageScoreArea}>
          <span className={styles.title}>全素養平均スコア</span>
          <div
            className={clsx(
              styles.scores,
              difference !== null && difference > 0 && styles.high,
              difference !== null && difference < 0 && styles.low,
            )}
          >
            {!averageScoreText ? (
              <div className={styles.averageScore}>-</div>
            ) : (
              <div className={styles.averageScore}>
                {averageScoreText.substring(0, 1)}
                <span className={styles.sub}>
                  {averageScoreText.substring(1)}
                </span>
              </div>
            )}
            <div className={styles.difference}>
              <span className={styles.title}>前回差</span>
              {difference === null ? (
                <span className={styles.score}>-&nbsp;</span>
              ) : (
                <span className={styles.score}>{`${
                  difference > 0 ? "+" : ""
                }${difference.toFixed(1)}`}</span>
              )}
            </div>
          </div>
        </div>
        <div className={styles.next_actions} key="current_next_actions">
          <div
            className={clsx(
              styles.collapse_title,
              "d-flex justify-content-between align-items-center",
            )}
            data-bs-toggle="collapse"
            data-bs-target="#item_current_next_actions"
            aria-expanded="true"
            aria-controls="item_current_next_actions"
          >
            <span>今回のまとめ</span>
            <ArrowDownIcon />
          </div>
          <div
            id="item_current_next_actions"
            className={`collapse show ${styles.listCurrentNextAction}`}
          >
            {nextActionKindsWithFilteredNextActions.map(k => {
              return (
                <div key={k.kind} className={styles.itemNextAction}>
                  <span className={styles.titleKind}>{k.shortTitle}</span>
                  {k.currentNextActions.map(content => (
                    <div
                      key={content.id}
                      className={clsx(styles.content, "d-flex flex-column")}
                    >
                      <div className={styles.contentKind}>
                        {content.next_action}
                      </div>
                      <span className={styles.date}>
                        {format(
                          new Date(content.created_at),
                          "yyyy.MM.dd HH:mm",
                        )}
                      </span>
                    </div>
                  ))}
                  {k.currentNextActions.length === 0 && (
                    <div className={styles.content}>
                      <div className={styles.noData}>未記入</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <ul
          className={clsx("nav nav-pills mt-3 mb-2", styles.tabs)}
          id="pills-tab"
          role="tablist"
        >
          <li className="nav-item">
            <a
              className="active"
              data-bs-toggle="pill"
              href="#pills-chart"
              role="tab"
              aria-controls="pills-chart"
              aria-selected="true"
            >
              サマリー
            </a>
          </li>
          <li className="nav-item">
            <a
              data-bs-toggle="pill"
              href="#pills-learn"
              role="tab"
              aria-controls="pills-learn"
              aria-selected="false"
            >
              学習したこと
            </a>
          </li>
          <li className="nav-item">
            <a
              data-bs-toggle="pill"
              href="#pills-past-next-actions"
              role="tab"
              aria-controls="pills-past-next-actions"
              aria-selected="false"
            >
              過去のまとめ
            </a>
          </li>
          <li className="nav-item">
            <a
              data-bs-toggle="pill"
              href="#pills-feedback"
              role="tab"
              aria-controls="pills-feedback"
              aria-selected="false"
            >
              メモ
            </a>
          </li>
        </ul>
        <div className={clsx("tab-content")} id="pills-tabContent">
          <div
            className="tab-pane fade show active"
            id="pills-chart"
            role="tabpanel"
            aria-labelledby="pills-chart-tab"
          >
            <div className={styles.chart_wrapper}>
              {!!assessment.id && (
                <div className={styles.assessmentTermWrapper}>
                  <p className={styles.assessmentCurrentTitle}>
                    {getTermName(assessment)}
                  </p>
                  {!!previousTerms.length && (
                    <DropdownTerm
                      previousTerms={previousTerms}
                      onChange={setSelectedTerms}
                    />
                  )}
                </div>
              )}
              <div className={styles.summary}>
                {currentDataChart && (
                  <BarChartTeacher
                    currentData={currentDataChart}
                    pastData={pastDataStudentChart}
                  />
                )}
              </div>
              <div className={styles.switch}>
                {X_LABELS_JSX.map((label, index: number) => (
                  <div
                    key={index}
                    className={clsx(
                      styles.switch__item,
                      assessment.id &&
                        !!assessment.assessment_experiences[index] &&
                        !!assessment.assessment_experiences[index].score &&
                        styles.active,
                    )}
                  >
                    <div>
                      <p>{index + 1}</p>
                      <span>{label}</span>
                    </div>
                  </div>
                ))}
                <div className={styles.switch__item} />
              </div>
            </div>
            <div className={styles.selectReasonWrapper}>
              <button
                className={clsx(
                  styles.selectReason,
                  selectedExperienceNo === 0 && styles.selected,
                )}
                onClick={() => setSelectedExperienceNo(0)}
              >
                ALL
              </button>
              {ASSESSMENT_CONTENTS.map(c => (
                <button
                  key={c.id}
                  className={clsx(
                    styles.selectReason,
                    selectedExperienceNo === c.id && styles.selected,
                  )}
                  style={
                    selectedExperienceNo === c.id
                      ? {
                          backgroundColor: LABEL_COLORS[c.id],
                          borderColor: LABEL_COLORS[c.id],
                        }
                      : {
                          color: LABEL_COLORS[c.id],
                          borderColor: LABEL_COLORS[c.id],
                        }
                  }
                  onClick={() => setSelectedExperienceNo(c.id)}
                >
                  {c.questionTitle}
                </button>
              ))}
            </div>
            <div className={styles.all_reasons}>
              {filteredReasons.map(reason => {
                return (
                  <div key={reason.id} className={styles.contentBlock}>
                    <div className={styles.labelArea}>
                      <AssessmentExperienceLabel
                        experienceNo={reason.experience_no}
                      />
                    </div>
                    <div className={styles.contentKind}>{reason.reason}</div>
                    <span className={styles.date}>
                      {format(new Date(reason.created_at), "yyyy.MM.dd HH:mm")}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div
            className="tab-pane fade"
            id="pills-learn"
            role="tabpanel"
            aria-labelledby="pills-learn-tab"
          >
            <div className={styles.learnList}>
              <span className={clsx(styles.title, "d-block mb-3")}>
                前回から今回までの間に学んだこと
              </span>
              {assessmentAnswers.map((answer, index) => (
                <div key={index} className={styles.learnBlock}>
                  <span className={clsx(styles.name, "d-block mb-3")}>
                    {`${answer.teaching_material_name} ${answer.lesson_name}`}
                  </span>
                  <div
                    className={clsx(
                      "d-block",
                      styles.answerList,
                      answer.contents.length === 2 &&
                        "d-flex justify-content-between",
                    )}
                  >
                    {answer.contents.map((question, index) => (
                      <div
                        key={index}
                        className={clsx(
                          "mb-3",
                          answer.contents.length === 2 && "w-50",
                        )}
                      >
                        <span className={styles.name}>
                          {QUESTION_TITLE[index]}
                        </span>
                        <p className={clsx(styles.content, "mt-2")}>
                          {question.content}
                        </p>
                      </div>
                    ))}
                  </div>
                  <span className={styles.date}>
                    {format(new Date(answer.created_at), "yyyy.MM.dd HH:mm")}
                  </span>
                </div>
              ))}
              {assessmentAnswers.length === 0 && (
                <div className={styles.learnBlock}>
                  <div className={styles.noData}>データなし</div>
                </div>
              )}
            </div>
          </div>
          <div
            className="tab-pane fade"
            id="pills-past-next-actions"
            role="tabpanel"
            aria-labelledby="pills-past-next-actions-tab"
          >
            <div className={styles.listNextAction}>
              <span className={clsx(styles.title, "d-block mb-3")}>
                過去のまとめ
              </span>
              {nextActionKindsWithFilteredNextActions.map(k => {
                return (
                  <div key={k.kind} className={styles.itemNextAction}>
                    <span className={styles.titleKind}>{k.shortTitle}</span>
                    {k.pastNextActions.map(content => (
                      <div key={content.id} className={styles.contentBlock}>
                        <div className={styles.contentKind}>
                          {content.next_action}
                        </div>
                        <span className={styles.date}>
                          {format(
                            new Date(content.created_at),
                            "yyyy.MM.dd HH:mm",
                          )}
                        </span>
                      </div>
                    ))}
                    {k.pastNextActions.length === 0 && (
                      <div className={styles.contentBlock}>
                        <div className={styles.noData}>未記入</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div
            className="tab-pane fade"
            id="pills-feedback"
            role="tabpanel"
            aria-labelledby="pills-feedback-tab"
          >
            <div className={clsx(styles.feedback, "mx-auto")}>
              <p>
                <span className={styles.feedbackTitle}>生徒に対してのメモ</span>
                <br />
                <span className={styles.feedbackNote}>
                  ※生徒の気付きや経験などに着目し、成長へのサポートを意識してご記入ください（記入した内容は生徒へ送信されません）
                </span>
              </p>
              <textarea
                value={feedback}
                onChange={e =>
                  setFeedback(e.target.value.replace(/\r\n|\r|\n/g, "\n"))
                }
                disabled={!assessment.id}
                placeholder={
                  "例：他者視点は、本人が感じているよりも意識できているので、三者面談でも伝える。／○○という小さな気付きから、自ら主体性を発揮するようになったのは素晴らしい。／自ら視野を広げる意識をさらに持ってほしいので、修学旅行前のHRで簡単なワークを実施する。"
                }
              />
              <button
                onClick={() => onSubmitFeedback(feedback)}
                disabled={
                  feedback.length === 0 ||
                  feedback.trim() ===
                    (assessment.id && (assessment.feedback_comment || ""))
                }
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </div>
      {pdfToken && (
        <ModalConfirm
          onClose={onClearPDFToken}
          title="ダウンロードしますか？"
          buttons={[
            {
              title: "キャンセル",
              action: onClearPDFToken,
              type: "None",
            },
            {
              title: "ダウンロード",
              action: () => {
                window.open(
                  `${API_URL}/assessments/download_student_pdf?class_id=${classID}&student_id=${currentStudent.id}&temporary_token=${pdfToken}`,
                  "_blank",
                );
                onClearPDFToken();
              },
              type: "Success",
            },
          ]}
        />
      )}
    </div>
  );
};

export default Assessment;
